import React from "react";
import { FORM_ERROR } from "final-form";
import { Field, Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { gql, useMutation } from "@apollo/client";
import { Dialog } from "@syos-music/common";

import { Alert, Grid, Fab, Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Delete as DeleteIcon, Plus as AddIcon } from "mdi-material-ui";

import MouthpieceLabel from "components/mouthpiece/MouthpieceLabel";
import MouthpieceField from "components/mouthpiece/MouthpieceField";

const CREATE_CLARINET_MOUTHPIECE_MUTATION = gql`
  mutation ($clarinetMouthpiece: ClarinetMouthpieceInput!) {
    CreateClarinetMouthpiece(clarinetMouthpiece: $clarinetMouthpiece) {
      clarinetMouthpiece {
        id
        clarinetType
        nomenclature
      }
    }
  }
`;

const CREATE_SAXOPHONE_MOUTHPIECE_MUTATION = gql`
  mutation ($saxophoneMouthpiece: SaxophoneMouthpieceInput!) {
    CreateSaxophoneMouthpiece(saxophoneMouthpiece: $saxophoneMouthpiece) {
      saxophoneMouthpiece {
        id
        saxophoneType
        nomenclature
      }
    }
  }
`;

export default function MouthpieceFieldDialog() {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  const [createClarinetMouthpiece] = useMutation(
    CREATE_CLARINET_MOUTHPIECE_MUTATION
  );
  const [createSaxophoneMouthpiece] = useMutation(
    CREATE_SAXOPHONE_MOUTHPIECE_MUTATION
  );

  return (
    <Field
      name="mouthpieceRef"
      isEqual={(a, b) =>
        a?.type === b?.type &&
        a?.clarinetMouthpiece?.id === b?.clarinetMouthpiece?.id &&
        a?.saxophoneMouthpiece?.id === b?.saxophoneMouthpiece?.id
      }
    >
      {({ input }) => (
        <>
          <Grid container alignItems="center">
            {!!input.value ? (
              <>
                <Grid item>
                  <Fab size="small" onClick={() => input.onChange(null)}>
                    <DeleteIcon />
                  </Fab>
                </Grid>
                <Grid item xs>
                  <MouthpieceLabel mouthpieceRef={input.value} />
                </Grid>
              </>
            ) : (
              <Grid item>
                <Button
                  color="primary"
                  size="small"
                  onClick={openDialog}
                  startIcon={<AddIcon />}
                >
                  Associer un bec
                </Button>
              </Grid>
            )}
          </Grid>

          <Dialog open={isDialogOpen} onClose={closeDialog}>
            <Form
              mutators={{ ...arrayMutators }}
              onSubmit={async (values) => {
                try {
                  switch (values.mouthpiece.type) {
                    case "clarinetMouthpiece": {
                      const { data } = await createClarinetMouthpiece({
                        variables: {
                          clarinetMouthpiece:
                            values.mouthpiece.clarinetMouthpiece,
                        },
                      });

                      const { clarinetMouthpiece } =
                        data?.CreateClarinetMouthpiece;

                      input.onChange({
                        type: "clarinetMouthpiece",
                        clarinetMouthpiece,
                        saxophoneMouthpiece: null,

                        instrument: "clarinet",
                        instrumentType: clarinetMouthpiece.clarinetType,
                        name: clarinetMouthpiece.nomenclature,
                      });
                      break;
                    }

                    case "saxophoneMouthpiece": {
                      const { data } = await createSaxophoneMouthpiece({
                        variables: {
                          saxophoneMouthpiece:
                            values.mouthpiece.saxophoneMouthpiece,
                        },
                      });

                      const { saxophoneMouthpiece } =
                        data?.CreateSaxophoneMouthpiece;

                      input.onChange({
                        type: "saxophoneMouthpiece",
                        clarinetMouthpiece: null,
                        saxophoneMouthpiece,

                        instrument: "saxophone",
                        instrumentType: saxophoneMouthpiece.saxophoneType,
                        name: saxophoneMouthpiece.nomenclature,
                      });
                      break;
                    }

                    default:
                      return {
                        [FORM_ERROR]: `Instrument inconnu: ${values.mouthpiece.type}`,
                      };
                  }

                  closeDialog();
                } catch (error) {
                  return true;
                }
              }}
              initialValues={{
                saxophoneMouthpiece: {},
                clarinetMouthpiece: {},
              }}
            >
              {({ handleSubmit, submitting, submitError }) => (
                <form onSubmit={handleSubmit}>
                  <Dialog.Title color="primary">Associer un bec</Dialog.Title>

                  <Dialog.Content>
                    <Grid container>
                      <Grid item xs={12}>
                        <MouthpieceField name="mouthpiece" required />
                      </Grid>

                      {submitError && (
                        <Grid item xs={12}>
                          <Alert severity="error">{submitError}</Alert>
                        </Grid>
                      )}
                    </Grid>
                  </Dialog.Content>

                  <Dialog.Actions>
                    <Button variant="text" onClick={closeDialog}>
                      Annuler
                    </Button>

                    <LoadingButton type="submit" loading={submitting}>
                      Créer
                    </LoadingButton>
                  </Dialog.Actions>
                </form>
              )}
            </Form>
          </Dialog>
        </>
      )}
    </Field>
  );
}

MouthpieceFieldDialog.fragments = {
  mouthpieceRef: gql`
    fragment MouthpieceFieldDialog_MouthpieceRef on MouthpieceRef {
      id
      type
      clarinetMouthpiece {
        id
      }
      saxophoneMouthpiece {
        id
      }

      ...MouthpieceLabel_mouthpieceRef
    }

    ${MouthpieceLabel.fragments.mouthpieceRef}
  `,
};
